import { Draft } from 'immer';
import {
  Area, Styles,
  StylesMatrix,
  Sheet,
  Style
} from '../../types';
import { SheetManagementSet } from '../../types/sheet/draft';
import {
  AreaStyle,
  CellStyle,
} from '../../types/commands/sheetCommands/sheetCommands';
import {
  convertArrayAreaAddressToArea,
  createArrayAreaAddressFromArea,
  getIntersectedArea,
  isEqualAreas,
  reduceAreaByIntersection,
} from '../area/area';
import _ from 'lodash';
import {
  addAddressToRangeMatrix,
  removeAddressFromRangeMatrix,
} from '../address/address';
import { cellStyleCommand } from '../commands/generate/generateSheetCommands';
import { convertAreaToAddressString } from '../address/singleAddress';

export async function applyStyles(
  set: SheetManagementSet,
  sheet: Draft<Sheet>,
  command: CellStyle
) {
  const { sheetId, styles: newStyles } = command;

  const { stylesMatrix, styles } = sheet;

  console.log(
    'styles',
    newStyles.map((format) => {
      return {
        address: convertAreaToAddressString(format.area, 100, 100),
        style: format.style,
      };
    })
  );

  const oldStyles: AreaStyle[] = [];
  for (const areaStyle of newStyles) {
    startUpdateStyle(stylesMatrix, styles, areaStyle, oldStyles);
  }

  console.log(
    'oldFormats',
    oldStyles.map((format) => {
      return {
        address: convertAreaToAddressString(format.area, 100, 100),
        style: format.style,
      };
    })
  );

  cellStyleCommand(set, sheetId, command, oldStyles);
}

export function startUpdateStyle(
  stylesMatrix: StylesMatrix,
  styles: Styles,
  areaStyle: AreaStyle,
  oldStyles: AreaStyle[]
) {
  const { area, style } = areaStyle;
  const address = createArrayAreaAddressFromArea(area);

  if (styles[address]) {
    updateExistingRangeStyle(
      stylesMatrix,
      styles,
      area,
      address,
      style,
      oldStyles
    );
  } else {
    saveStyleByResolvingIntersection(
      stylesMatrix,
      styles,
      area,
      style,
      oldStyles
    );
  }
}

export const saveStyleByResolvingIntersection = (
  stylesMatrix: StylesMatrix,
  styles: Styles,
  newArea: Area,
  newStyle: Style,
  oldStyles: AreaStyle[]
) => {
  const existingStyles = Object.keys(styles);
  for (const existingAddress of existingStyles) {
    const style = styles[existingAddress];
    const existingArea = convertArrayAreaAddressToArea(existingAddress);
    if (!existingArea) continue;

    const intersection = getIntersectedArea(newArea, existingArea);
    if (intersection) {
      // Remove from current matrix
      removeAddressFromRangeMatrix(stylesMatrix, existingArea, existingAddress);
      reduceAreaByIntersection(existingArea, intersection).forEach((area) =>
        startSaveNonIntersectedStyle(stylesMatrix, styles, area, style)
      );
      saveStyleInOldStyles(oldStyles, intersection, _.cloneDeep(style));

      // oldStyles.push({
      //   area: intersection,
      //   style: _.cloneDeep(style),
      // });
      delete styles[existingAddress];
    }
  }

  const resetStyle = updateStyleProperties(newStyle, {});

  startSaveNonIntersectedStyle(
    stylesMatrix,
    styles,
    newArea,
    resetStyle.newStyle
  );

  saveStyleInOldStyles(oldStyles, newArea, resetStyle.oldStyle, false);

  return oldStyles;
};

function startSaveNonIntersectedStyle(
  stylesMatrix: StylesMatrix,
  styles: Styles,
  area: Area,
  style: Style
) {
  if (!_.isEmpty(style)) {
    const arrayAreaAddress = createArrayAreaAddressFromArea(area);
    addAddressToRangeMatrix(stylesMatrix, area, arrayAreaAddress);
    styles[arrayAreaAddress] = _.cloneDeep(style);
  }
}

function updateExistingRangeStyle(
  stylesMatrix: StylesMatrix,
  styles: Styles,
  area: Area,
  address: string,
  style: Style,
  oldStyles: AreaStyle[]
) {
  const existingStyle = styles[address];
  const { oldStyle, newStyle } = updateStyleProperties(style, existingStyle);
  saveStyleInOldStyles(oldStyles, area, oldStyle);

  if (_.isEmpty(newStyle)) {
    delete styles[address];
    removeAddressFromRangeMatrix(stylesMatrix, area, address);
  } else {
    styles[address] = newStyle;
  }
}

const saveStyleInOldStyles = (
  oldStyles: AreaStyle[],
  area: Area,
  style: Style,
  isPush = true
) => {
  const index = oldStyles.findIndex((oldFormat) =>
    isEqualAreas(area, oldFormat.area)
  );
  if (index === -1) {
    if (isPush)
      oldStyles.push({
        area: area,
        style,
      });
    else {
      oldStyles.unshift({
        area,
        style,
      });
    }
  }
};

const updateStyleProperties = (
  style: Style,
  cellStyle: any = {}
): {
  oldStyle: Style;
  newStyle: Style;
} => {
  cellStyle = _.cloneDeep(cellStyle);

  const oldStyle: any = {};
  Object.keys(style).forEach((key1) => {
    const key = key1 as keyof Style;
    if (style[key]) {
      if (typeof style[key] === 'object') {
        const parentProperty = style[key] as any;
        Object.keys(parentProperty).forEach((childKey) => {
          // Font  | Border  |Alignment | Fill
          const key2 = childKey;

          const value = parentProperty[key2];

          if (!oldStyle[key]) oldStyle[key] = {};
          oldStyle[key][key2] = cellStyle[key]?.[key2]; //  ?? defaultStyle[key]?.[key2]

          if (value === null) {
            delete cellStyle[key]?.[key2];
            if (_.isEmpty(cellStyle[key])) delete cellStyle[key];
          } else {
            if (!cellStyle[key]) cellStyle[key] = {};
            cellStyle[key][key2] = value;
          }

          // if (!value || value === defaultStyle[key]?.[key2])
          //   delete cellStyle[key]?.[key2];
        });

        if (_.isEmpty(cellStyle[key])) delete cellStyle[key];
        // const childPropertyKeys =
      } else {
        // It can be textWrap | indendation | rotation
        const value = style[key];
        oldStyle[key] = cellStyle[key]; //?? defaultStyle[key];
        if (value === null)
          delete cellStyle[key]; // || value === defaultStyle[key]
        else cellStyle[key] = value;
      }
    }
  });

  return {
    oldStyle: <Style>oldStyle,
    newStyle: <Style>cellStyle,
  };
};
